import { getDefaultMoneyTarget } from './moneyTargetDefaults';

/**
 * The money target for one location and year.
 *
 * INTERIM STORAGE. The target belongs on the scenario, alongside locationId,
 * name and year, and should travel on the year plan payload so it saves and
 * discards with the rest of the plan. That backend field does not exist yet
 * (REH-340, with the frontend swap in REH-341), so until it lands the value is held per browser and the
 * mock supplies the starting figure.
 *
 * Everything else goes through this seam, so swapping it for the endpoint is a
 * change to these two functions and nothing else. Both callers by name:
 * `useYearPlanTarget` (read and write, on the year plan) and
 * `mockOverviewService` (read only, so an edited target shows on the cards).
 */

const STORAGE_PREFIX = 'foniks.moneyTarget';

function storageKey(locationId: string, year: number): string {
  return `${STORAGE_PREFIX}.${locationId}.${String(year)}`;
}

// Absent or unreadable storage means "nothing saved here", which falls through
// to the mock's figure rather than failing the page. Malformed storage is
// different: something was written, so it is read as "no target" rather than
// silently resurrecting the default.
export function getMoneyTarget(locationId: string, year: number): number | null {
  try {
    const stored = globalThis.localStorage.getItem(storageKey(locationId, year));
    if (stored === null) return getDefaultMoneyTarget(locationId);
    if (stored === '') return null;
    const parsed = Number(stored);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch (e) {
    console.error('Failed to read the money target:', e);
    return getDefaultMoneyTarget(locationId);
  }
}

/**
 * Stores the target, and reports whether it actually landed.
 *
 * A null target is stored explicitly rather than deleted: clearing the field
 * means the location has no target, which is a different state from never having
 * set one and must not fall back to the mock. Zero is coerced to null, because
 * nothing in the stack accepts a ceiling of zero and a stored "0" would read
 * back as null on the next load anyway.
 *
 * Returns false when the browser refuses storage. The caller needs that: marking
 * the field clean on a failed write would show the planner a saved target that
 * is not saved, and would disarm the unsaved-changes guard protecting it.
 */
export function saveMoneyTarget(
  locationId: string,
  year: number,
  target: number | null
): boolean {
  const stored = target === null || target <= 0 ? '' : String(target);
  try {
    globalThis.localStorage.setItem(storageKey(locationId, year), stored);
    return true;
  } catch (e) {
    console.error('Failed to store the money target:', e);
    return false;
  }
}
